// Keep this protocol and decision engine identical in web and MobileCh3.
export interface RepairCandidate {
    readonly callId: number;
    readonly roomId: string;
    readonly callState: string;
    readonly startedAt: string;
    readonly participantIds: readonly number[];
    readonly alreadyHangedUpUsers: readonly number[];
    readonly matrixCallId?: string;
}

export interface RepairTimelineEvent {
    readonly eventId: string;
    readonly sender: string;
    readonly timestamp: number;
    readonly type: string;
    readonly content: Readonly<Record<string, unknown>>;
}

export interface CallRepair {
    readonly callId: number;
    readonly roomId: string;
    readonly matrixCallId: string;
    readonly participantId: number;
    readonly terminalType: 'HANGUP' | 'REJECT' | 'TECHNICAL_CLOSURE';
    readonly occurredAt: number;
    readonly evidence: {
        readonly kind: 'MATRIX_EVENT' | 'LOCAL_TERMINATION';
        readonly eventId?: string;
        readonly sessionId?: string;
        readonly reason?: 'USER_HANGUP' | 'USER_REJECT' | 'SESSION_ENDED';
        readonly metadataEventId?: string;
        readonly inviteEventId?: string;
        readonly sessionEventId?: string;
    };
}

export interface RepairStorage {
    get(key: string): string | undefined;
    set(key: string, value: string): void;
    remove(key: string): void;
    keys(prefix: string): readonly string[];
}

export interface RecordedLocalTermination {
    readonly scope: string;
    readonly matrixCallId: string;
    readonly roomId: string;
    readonly sessionId: string;
    readonly terminalType: CallRepair['terminalType'];
    readonly occurredAt: number;
    readonly callId?: number;
}

export function recordLocalCallTermination(storage: RepairStorage, record: RecordedLocalTermination): void {
    if (!record.sessionId || !record.matrixCallId || !Number.isFinite(record.occurredAt)) return;
    const key = record.scope + ':local:' + JSON.stringify(record.matrixCallId) + ':' + JSON.stringify(record.sessionId);
    // A later cleanup callback must not overwrite the original intent or its occurrence time.
    if (!storage.get(key)) storage.set(key, JSON.stringify(record));
}

export interface RepairPorts {
    readonly scope: string;
    readonly reporter: number;
    readonly storage: RepairStorage;
    ready(): boolean;
    active(candidate: RepairCandidate): boolean;
    candidates(signal: AbortSignal): Promise<readonly RepairCandidate[]>;
    page(request: {candidate: RepairCandidate; cursor?: string; signal: AbortSignal}): Promise<{
        readonly events: readonly RepairTimelineEvent[];
        readonly next?: string;
        readonly oldestTimestamp: number;
    }>;
    resolveParticipant(matrixUserId: string): Promise<number | undefined>;
    submit(repair: CallRepair, signal: AbortSignal): Promise<void>;
    report(candidate: RepairCandidate | undefined, reason: string): void;
}

interface Scan {
    cursor?: string;
    events: RepairTimelineEvent[];
}

function readScan(value: string | undefined): Scan {
    if (!value) return {events: []};
    const saved: unknown = JSON.parse(value);
    if (!isRepairRecord(saved) || !Array.isArray(saved.events) || saved.events.length > 2_000 ||
        (saved.cursor !== undefined && typeof saved.cursor !== 'string')) throw new Error('Invalid repair cursor');
    const events = saved.events.map((event: unknown): RepairTimelineEvent => {
        if (!isRepairRecord(event) || typeof event.eventId !== 'string' || typeof event.sender !== 'string' ||
            typeof event.timestamp !== 'number' || !Number.isFinite(event.timestamp) ||
            typeof event.type !== 'string' || !isRepairRecord(event.content)) throw new Error('Invalid repair evidence');
        return retainRepairEvidence({eventId: event.eventId, sender: event.sender, timestamp: event.timestamp,
            type: event.type, content: event.content});
    });
    return {events, cursor: saved.cursor};
}

function readLocalTermination(value: string | undefined): RecordedLocalTermination {
    const record: unknown = JSON.parse(value ?? '');
    if (!isRepairRecord(record) || typeof record.scope !== 'string' || typeof record.matrixCallId !== 'string' ||
        typeof record.roomId !== 'string' || typeof record.sessionId !== 'string' || !record.sessionId ||
        typeof record.occurredAt !== 'number' || !Number.isFinite(record.occurredAt) ||
        !['HANGUP', 'REJECT', 'TECHNICAL_CLOSURE'].includes(String(record.terminalType)) ||
        (record.callId !== undefined && typeof record.callId !== 'number')) throw new Error('Invalid local termination');
    return {scope: record.scope, matrixCallId: record.matrixCallId, roomId: record.roomId, sessionId: record.sessionId,
        occurredAt: record.occurredAt, callId: record.callId,
        terminalType: record.terminalType === 'HANGUP' ? 'HANGUP' : record.terminalType === 'REJECT' ? 'REJECT' : 'TECHNICAL_CLOSURE'};
}

const relevant = (event: RepairTimelineEvent): boolean =>
    ['m.call.invite', 'm.call.answer', 'm.call.select_answer', 'm.call.hangup', 'm.call.reject',
        'org.phonehq.metadata'].includes(event.type);

export const isRepairRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

export const retainRepairEvidence = (event: RepairTimelineEvent): RepairTimelineEvent => ({
    ...event,
    content: Object.fromEntries(Object.entries(event.content).filter(([name]) =>
        ['call_id', 'callId', 'matrixCallId', 'party_id', 'selected_party_id', 'reason'].includes(name))),
});

function correlate(candidate: RepairCandidate, events: readonly RepairTimelineEvent[]) {
    if (candidate.matrixCallId) return {
        matrixCallId: candidate.matrixCallId, metadataEventId: undefined, inviteEventId: undefined,
    };
    const metadata = events.filter(event => event.type === 'org.phonehq.metadata' &&
        String(event.content.callId) === String(candidate.callId));
    if (metadata.length !== 1) return undefined;
    const record = metadata[0];
    const after = events.filter(event => event.timestamp >= record.timestamp &&
        event.timestamp <= record.timestamp + 30_000 && event.eventId !== record.eventId)
        .sort((left, right) => left.timestamp - right.timestamp);
    const next = after[0];
    if (next?.type !== 'm.call.invite' || next.sender !== record.sender ||
        typeof next.content.call_id !== 'string') return undefined;
    if (record.content.matrixCallId && record.content.matrixCallId !== next.content.call_id) return undefined;
    return {matrixCallId: next.content.call_id, metadataEventId: record.eventId, inviteEventId: next.eventId};
}

function selectedAnswer(events: readonly RepairTimelineEvent[]): RepairTimelineEvent | undefined {
    const caller = events.find(event => event.type === 'm.call.invite')?.sender;
    return events.filter(event => event.type === 'm.call.select_answer' && (!caller || event.sender === caller))
        .sort((left, right) => right.timestamp - left.timestamp)[0];
}

export async function decideCallRepairs(request: {
    candidate: RepairCandidate;
    events: readonly RepairTimelineEvent[];
    resolveParticipant: RepairPorts['resolveParticipant'];
}): Promise<readonly CallRepair[]> {
    const {candidate, events, resolveParticipant} = request;
    const identity = correlate(candidate, events);
    if (!identity) return [];
    const callEvents = events.filter(event => event.content.call_id === identity.matrixCallId);
    const repairs: CallRepair[] = [];
    const repaired = new Set(candidate.alreadyHangedUpUsers);
    for (const event of [...callEvents].sort((left, right) => left.timestamp - right.timestamp)) {
        if (event.type !== 'm.call.hangup' && event.type !== 'm.call.reject') continue;
        if (['answered_elsewhere', 'replaced'].includes(String(event.content.reason))) continue;
        const participant = await resolveParticipant(event.sender);
        if (!participant || !candidate.participantIds.includes(participant) || repaired.has(participant)) continue;
        const answers = callEvents.filter(item => item.type === 'm.call.answer' && item.sender === event.sender);
        const selectedParty = selectedAnswer(callEvents)?.content.selected_party_id;
        const answerParties = new Set(answers.map(answer => answer.content.party_id));
        if (answerParties.size > 1 && !selectedParty) continue;
        const participatingParty = selectedParty ?? answers[0]?.content.party_id;
        if (answers.length && participatingParty && event.content.party_id !== participatingParty) continue;
        const invite = callEvents.find(item => item.type === 'm.call.invite' && item.sender === event.sender);
        if (invite?.content.party_id && event.content.party_id !== invite.content.party_id) continue;
        repairs.push({
            callId: candidate.callId, roomId: candidate.roomId, matrixCallId: identity.matrixCallId,
            participantId: participant, terminalType: event.type === 'm.call.hangup' ? 'HANGUP' : 'REJECT',
            occurredAt: event.timestamp,
            evidence: {kind: 'MATRIX_EVENT', eventId: event.eventId,
                sessionId: typeof event.content.party_id === 'string' ? event.content.party_id : undefined,
                metadataEventId: identity.metadataEventId, inviteEventId: identity.inviteEventId},
        });
        repaired.add(participant);
    }
    return repairs;
}

export class CallStateRepairWorker {
    private running = false;
    private stopped = false;
    private requested = false;
    private controller?: AbortController;
    private offset = 0;

    constructor(private readonly ports: RepairPorts) {}

    stop(): void {
        this.stopped = true;
        this.controller?.abort();
    }

    async run(): Promise<void> {
        if (this.stopped || !this.ports.ready()) return;
        if (this.running) { this.requested = true; return; }
        this.running = true;
        this.controller = new AbortController();
        const signal = this.controller.signal;
        try {
            const candidates = await this.bounded(signal, child => this.ports.candidates(child));
            const ongoingIds = new Set(candidates.map(candidate => candidate.callId));
            for (const key of this.ports.storage.keys(this.ports.scope + ':local:')) {
                try {
                    const record = readLocalTermination(this.ports.storage.get(key));
                    if (record.callId && !ongoingIds.has(record.callId)) this.ports.storage.remove(key);
                } catch { this.ports.report(undefined, 'invalid_local_record'); }
            }
            const batch = [...candidates.slice(this.offset), ...candidates.slice(0, this.offset)].slice(0, 20);
            this.offset = candidates.length ? (this.offset + batch.length) % candidates.length : 0;
            for (const candidate of batch) {
                if (this.stopped || !this.ports.ready()) break;
                try { await this.bounded(signal, child => this.reconcile(candidate, child)); }
                catch { this.ports.report(candidate, signal.aborted ? 'cancelled' : 'evidence_unavailable'); }
            }
        } catch {
            this.ports.report(undefined, 'poll_failed');
        } finally {
            this.running = false;
            if (this.requested) {
                this.requested = false;
                if (!this.stopped) void this.run();
            }
        }
    }

    private async bounded<T>(parent: AbortSignal, operation: (signal: AbortSignal) => Promise<T>): Promise<T> {
        const controller = new AbortController();
        const cancel = () => controller.abort();
        parent.addEventListener('abort', cancel);
        let timer: ReturnType<typeof setTimeout> | undefined;
        try {
            return await Promise.race([
                operation(controller.signal),
                new Promise<never>((_resolve, reject) => {
                    timer = setTimeout(() => { controller.abort(); reject(new Error('Evidence deadline exceeded')); }, 15_000);
                }),
            ]);
        } finally {
            if (timer) clearTimeout(timer);
            controller.abort();
            parent.removeEventListener('abort', cancel);
        }
    }

    private async reconcile(candidate: RepairCandidate, signal: AbortSignal): Promise<void> {
        if (!candidate.participantIds?.includes(this.ports.reporter) || !Number.isFinite(Date.parse(candidate.startedAt))) {
            this.ports.report(candidate, 'identity_unavailable'); return;
        }
        if (this.ports.active(candidate)) { this.ports.report(candidate, 'active_call'); return; }
        const key = this.ports.scope + ':scan:' + candidate.callId;
        const saved = this.ports.storage.get(key);
        let scan: Scan;
        try { scan = readScan(saved); }
        catch {
            this.ports.storage.remove(key);
            this.ports.report(candidate, 'invalid_history_cursor');
            scan = {events: []};
        }
        // Refresh the head even while resuming older history: a peer may have ended since the previous pass.
        for (let pageIndex = 0; pageIndex < 3; pageIndex += 1) {
            const cursor = pageIndex === 0 ? undefined : scan.cursor;
            const page = await this.ports.page({candidate, cursor, signal});
            if (signal.aborted) return;
            const byId = new Map(scan.events.map(event => [event.eventId, event]));
            page.events.filter(relevant).forEach(event => byId.set(event.eventId, retainRepairEvidence(event)));
            scan.events = [...byId.values()];
            if (scan.events.length > 2_000) {
                this.ports.storage.remove(key);
                this.ports.report(candidate, 'history_bound_exceeded'); return;
            }
            const complete = !page.next || page.oldestTimestamp < Date.parse(candidate.startedAt) - 30_000;
            if (complete) {
                const repairs = await decideCallRepairs({
                    candidate, events: scan.events, resolveParticipant: id => this.ports.resolveParticipant(id),
                });
                const localRepairs = await this.repairLocalTerminations({candidate, events: scan.events, signal});
                for (const repair of repairs) {
                    if (signal.aborted || this.stopped || !this.ports.ready() || this.ports.active(candidate)) return;
                    await this.ports.submit(repair, signal);
                    this.ports.report(candidate, 'repair_accepted_awaiting_state');
                }
                this.ports.storage.remove(key);
                if (!repairs.length && !localRepairs) this.ports.report(candidate, 'no_terminal_evidence');
                return;
            }
            if (pageIndex > 0 && page.next === cursor) throw new Error('Pagination did not advance');
            if (pageIndex > 0 || !scan.cursor) scan.cursor = page.next;
            this.ports.storage.set(key, JSON.stringify(scan));
        }
        this.ports.report(candidate, 'history_incomplete');
    }

    private async repairLocalTerminations(request: {
        candidate: RepairCandidate; events: readonly RepairTimelineEvent[]; signal: AbortSignal;
    }): Promise<number> {
        const {candidate, events, signal} = request;
        let accepted = 0;
        const identity = correlate(candidate, events);
        if (!identity) return accepted;
        const keys = this.ports.storage.keys(this.ports.scope + ':local:' + JSON.stringify(identity.matrixCallId) + ':');
        for (const key of keys) {
            try {
                const record = readLocalTermination(this.ports.storage.get(key));
                if (record.roomId !== candidate.roomId || record.scope !== this.ports.scope || !record.sessionId) continue;
                if (candidate.alreadyHangedUpUsers.includes(this.ports.reporter)) {
                    this.ports.storage.remove(key); continue;
                }
                const callEvents = events.filter(event => event.content.call_id === identity.matrixCallId);
                const selection = selectedAnswer(callEvents);
                const ownSessionEvents = events.filter(event => event.content.call_id === identity.matrixCallId &&
                    event.content.party_id === record.sessionId && ['m.call.invite', 'm.call.answer'].includes(event.type));
                const sessionEvent = ownSessionEvents[0];
                const ownerAnswers = events.filter(event => event.type === 'm.call.answer' &&
                    event.content.call_id === identity.matrixCallId && event.sender === sessionEvent?.sender);
                const caller = callEvents.find(event => event.type === 'm.call.invite');
                const isCaller = caller && await this.ports.resolveParticipant(caller.sender) === this.ports.reporter;
                if (!isCaller && selection && selection.content.selected_party_id !== record.sessionId) continue;
                if (!isCaller && new Set(ownerAnswers.map(answer => answer.content.party_id)).size > 1 && !selection) continue;
                if (!sessionEvent && !isCaller && callEvents.some(event => event.type === 'm.call.answer')) continue;
                if (signal.aborted || this.stopped || !this.ports.ready() || this.ports.active(candidate)) return accepted;
                const repair: CallRepair = {
                    callId: candidate.callId, matrixCallId: identity.matrixCallId, roomId: candidate.roomId,
                    participantId: this.ports.reporter, terminalType: record.terminalType, occurredAt: record.occurredAt,
                    evidence: {kind: 'LOCAL_TERMINATION', sessionId: record.sessionId,
                        sessionEventId: sessionEvent?.eventId, metadataEventId: identity.metadataEventId,
                        inviteEventId: identity.inviteEventId,
                        reason: record.terminalType === 'HANGUP' ? 'USER_HANGUP' :
                            record.terminalType === 'REJECT' ? 'USER_REJECT' : 'SESSION_ENDED'},
                };
                this.ports.storage.set(key, JSON.stringify({...record, callId: candidate.callId}));
                await this.ports.submit(repair, signal);
                accepted += 1;
                this.ports.report(candidate, 'local_repair_accepted_awaiting_state');
            } catch { this.ports.report(candidate, 'local_evidence_deferred'); }
        }
        return accepted;
    }
}
